<template>
  <div id="app" class="app-container">
    <div class="app-loader" v-if="initializing">
      <div class="app-loader-content">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Lädt...</span>
        </div>
        <p class="mt-2">Anwendung wird initialisiert...</p>
      </div>
    </div>
    
    <div v-else class="d-flex flex-column min-vh-100">
      <header v-if="showHeader" class="app-header">
        <app-header :sidebar-collapsed="sidebarCollapsed" @toggle-sidebar="toggleSidebar" />
      </header>
      
      <div class="d-flex flex-grow-1">
        <app-sidebar 
          v-if="showSidebar" 
          :collapsed="sidebarCollapsed" 
          @toggle-sidebar="toggleSidebar" 
          @hover="handleSidebarHover" 
        />
        
        <main class="app-content flex-grow-1" :class="{
          'content-expanded': sidebarCollapsed && !sidebarHover,
          'content-normal': !sidebarCollapsed || sidebarHover
        }">
          <router-view v-slot="{ Component }">
            <transition name="fade" mode="out-in">
              <component :is="Component" />
            </transition>
          </router-view>
        </main>
      </div>
      
      <footer v-if="showFooter" class="app-footer" :class="{
        'content-expanded': sidebarCollapsed && !sidebarHover,
        'content-normal': !sidebarCollapsed || sidebarHover
      }">
        <app-footer />
      </footer>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';
import AppHeader from '@/components/layout/AppHeader.vue';
import AppSidebar from '@/components/layout/AppSidebar.vue';
import AppFooter from '@/components/layout/AppFooter.vue';
import { useAuthStore } from '@/stores/auth';
import { useCompanyStore } from '@/stores/company';
import eventBus, { EventTypes } from '@/services/eventBus';

const initializing = ref(true);
const sidebarCollapsed = ref(true); // Standardmäßig eingeklappt
const sidebarHover = ref(false);
const publicRoutes = [
  '/login',
  '/register',
  '/password/reset',
  '/test'
];

const route = useRoute();
const authStore = useAuthStore();
const companyStore = useCompanyStore();

// Computed properties
const isPublicRoute = computed(() => {
  const result = publicRoutes.some(path => 
    route.path.startsWith(path) ||
    route.matched.some(record => record.meta.public)
  );
  console.log('isPublicRoute:', result, 'für Pfad:', route.path);
  return result;
});

const isAuthenticated = computed(() => {
  const result = authStore.isAuthenticated;
  console.log('isAuthenticated:', result, 'Token:', !!authStore.token, 'User:', !!authStore.user);
  return result;
});

// Temporäre Lösung: Zeige Header immer an, außer bei Login-bezogenen Routen
const showHeader = computed(() => {
  // Explizite Ausnahmen für Login-Seiten
  if (route.path === '/login' || route.path === '/register' || route.path === '/password/reset') {
    return false;
  }
  return true; // Für alle anderen Routen Header anzeigen
});

// Temporäre Lösung: Zeige Sidebar immer an, außer bei Login-bezogenen Routen
const showSidebar = computed(() => {
  // Explizite Ausnahmen für Login-Seiten
  if (route.path === '/login' || route.path === '/register' || route.path === '/password/reset') {
    return false;
  }
  return true; // Für alle anderen Routen Sidebar anzeigen
});

const showFooter = computed(() => {
  // Prüfe das Meta-Feld der aktuellen Route
  const shouldHide = route.meta.hideFooter === true;
  console.log('showFooter Check: Route Meta:', route.meta, 'hideFooter:', shouldHide);
  return !shouldHide; // Zeige Footer an, wenn hideFooter NICHT true ist
});

// Methoden
const toggleSidebar = (collapsed) => {
  if (collapsed !== undefined) {
    sidebarCollapsed.value = collapsed;
  } else {
    sidebarCollapsed.value = !sidebarCollapsed.value;
  }
  localStorage.setItem('sidebarCollapsed', String(sidebarCollapsed.value));
};

const handleSidebarHover = (isHovered) => {
  sidebarHover.value = isHovered;
};

const initializeTheme = () => {
  // Standard-Themenfarbe festlegen
  document.documentElement.style.setProperty('--primary-color', '#3490dc');
  
  // Firmen-spezifische Theme-Farbe anwenden, wenn verfügbar
  const company = companyStore.currentCompany;
  
  if (company && company.primary_color) {
    document.documentElement.style.setProperty('--primary-color', company.primary_color);
  }
};

// Watch für Farbänderungen
watch(() => companyStore.currentCompany?.primary_color, (newColor) => {
  if (newColor) {
    document.documentElement.style.setProperty('--primary-color', newColor);
  }
});

// Initialisierung
onMounted(() => {
  // Sidebar-Zustand aus localStorage wiederherstellen
  const savedSidebarState = localStorage.getItem('sidebarCollapsed');
  if (savedSidebarState !== null) {
    sidebarCollapsed.value = savedSidebarState === 'true';
  }

  // Event-Listener und andere Initialisierungen...
  eventBus.addEventListener(EventTypes.AUTH_IMPERSONATED, handleAuthEvent);
  eventBus.addEventListener(EventTypes.AUTH_CHANGED, handleAuthEvent);
  
  // Cleanup bei Komponenten-Zerstörung
  onBeforeUnmount(() => {
    eventBus.removeEventListener(EventTypes.AUTH_IMPERSONATED, handleAuthEvent);
    eventBus.removeEventListener(EventTypes.AUTH_CHANGED, handleAuthEvent);
  });
  
  // App-Initialisierung
  setTimeout(() => {
    initializing.value = false;
    initializeTheme();
  }, 500);
});

// Handler für Auth-Events
const handleAuthEvent = async (event) => {
  try {
    console.log('Auth-Event in App.vue empfangen:', event.type, event.detail);
    
    // Company-Store abrufen und Daten neu laden
    const companyStore = useCompanyStore();
    
    // Firmendaten neu laden
    if (companyStore) {
      // Cache leeren, um Neuladen zu erzwingen
      companyStore.lastFetch = null;
      await companyStore.fetchOwnCompany();
      console.log('Firmendaten nach Auth-Event erfolgreich aktualisiert');
    }
  } catch (err) {
    console.error('Fehler beim Aktualisieren der Daten nach Auth-Event:', err);
  }
};
</script>

<style lang="scss">
:root {
  --header-height: 70px;
  --sidenav-width-expanded: 260px;
  --sidenav-width-collapsed: 70px;
  --sidenav-animation-duration: 0.3s;
}

.app-container {
  min-height: 100vh;
}

.app-loader {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
}

.app-loader-content {
  text-align: center;
}

.app-content {
  padding: 20px;
  transition: margin-left var(--sidenav-animation-duration) ease;
  margin-left: var(--sidenav-width-collapsed);
  will-change: margin-left;
  
  &.content-expanded {
    margin-left: var(--sidenav-width-collapsed);
  }
  
  &.content-normal {
    margin-left: var(--sidenav-width-expanded);
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>

<style>
html, body {
  margin: 0;
  padding: 0;
  height: 100%;
  width: 100%;
}

#app {
  min-height: 100vh;
  margin: 0;
  padding: 0;
}

footer .container {
  margin-left: 200px !important;
}
</style> 